import { z } from "zod";
import { COMPANY_INGREDIENT_STATUSES, LOCATION_TYPES, MASTER_APPROVAL_STATUSES, PASSOVER_STATUSES } from "../enums.ts";
import { DateOnly } from "./common.ts";

export const MasterIngredientInput = z.object({
  name: z.string().min(1).max(300),
  vendor: z.string().max(500).nullable().optional(), // the legacy portal keeps the vendor's address in the name (2026-11-03)
  subVendor: z.string().max(500).nullable().optional(),
  brand: z.string().max(200).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  state: z.string().max(120).nullable().optional(),
  country: z.string().max(80).nullable().optional(),
  agency: z.string().max(300).nullable().optional(), // several certifying agencies, joined, from the legacy portal (2026-11-03)
  group: z.string().max(60).nullable().optional(),
  designation: z.string().max(60).nullable().optional(),
  restriction: z.string().max(60).nullable().optional(),
  category: z.string().max(80).nullable().optional(),
  certExpDate: DateOnly.nullable().optional(),
  fish: z.boolean().default(false),
  passover: z.boolean().default(false),
  passoverStatus: z.enum(PASSOVER_STATUSES).default("not_assessed"),
  passoverNotes: z.string().max(2000).nullable().optional(),
  passoverCertExpDate: DateOnly.nullable().optional(),
  pasYisroel: z.boolean().default(false),
  cholovYisroel: z.boolean().default(false),
  mevushal: z.boolean().default(false),
  kitniyos: z.boolean().default(false),
  yoshon: z.enum(["na", "yes", "no"]).default("na"),
  reviewDate: DateOnly.nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  adminNotes: z.string().max(4000).nullable().optional(),
  approvalStatus: z.enum(MASTER_APPROVAL_STATUSES).default("per_location"),
  externalIds: z.array(z.object({ agency: z.string().max(60), externalId: z.string().max(80) })).default([]),
  upcs: z.array(z.object({ upc: z.string().max(40), description: z.string().max(120).nullable().optional() })).default([]),
});
export type MasterIngredientInput = z.infer<typeof MasterIngredientInput>;
export const MasterIngredientUpdateInput = MasterIngredientInput.partial();

export const IngredientQuery = z.object({
  q: z.string().max(200).optional(),
  agency: z.string().max(60).optional(),
  group: z.string().max(60).optional(),
  designation: z.string().max(60).optional(),
  restriction: z.string().max(60).optional(),
  passover: z.enum(["kfp", "not_kfp"]).optional(),
  status: z.string().max(40).optional(),
  yoshon: z.enum(["yes", "no", "na"]).optional(),
  scope: z.enum(["master", "company", "all"]).default("all"),
  companyId: z.string().uuid().optional(),
  sort: z.enum(["name", "vkd", "cert_exp", "updated"]).default("name"),
  /** a location's linked ingredients, optionally by their standing there */
  locationType: z.enum(LOCATION_TYPES).optional(),
  locationId: z.string().uuid().optional(),
  locationStatus: z.enum(["approved", "rejected", "unassigned"]).optional(),
});
export type IngredientQuery = z.infer<typeof IngredientQuery>;

export const CompanyIngredientInput = z.object({
  companyId: z.string().uuid(),
  masterIngredientId: z.string().uuid().nullable().optional(),
  /** When no master exists yet, the master is created on first submit from these. */
  newMaster: MasterIngredientInput.partial().extend({ name: z.string().min(1).max(300) }).optional(),
  nameOverride: z.string().max(300).nullable().optional(),
  rmc: z.string().max(60).nullable().optional(),
  applyAllProducts: z.boolean().default(false),
  neededForPassover: z.boolean().default(false),
  notes: z.string().max(4000).nullable().optional(),
});
export type CompanyIngredientInput = z.infer<typeof CompanyIngredientInput>;

export const IngredientDecisionInput = z.object({
  decision: z.enum(["approve", "reject"]),
  reason: z.string().max(2000).optional(),
  passoverStatus: z.enum(PASSOVER_STATUSES).optional(),
});

export const IngredientLocationApprovalInput = z.object({
  locationType: z.enum(LOCATION_TYPES),
  locationId: z.string().uuid(),
  approvalStatus: z.string().max(60),
  passoverStatus: z.enum(PASSOVER_STATUSES).optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const CompanyIngredientStatusInput = z.object({
  status: z.enum(COMPANY_INGREDIENT_STATUSES),
  reason: z.string().max(2000).optional(),
});
